/**
 * LogisticsQuickBook — modal launched from the Home page when a user picks
 * one of the three logistics categories (Same-day Package / Courier / Business
 * Logistics). Collects pickup + delivery addresses, priority preference, and
 * auto-picks the nearest matching vendor (with an override list), then
 * redirects to /book/:vendorId with prefilled query params.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bike, Package, Truck, MapPin, Zap, ChevronRight, Loader2, ArrowLeft } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import AddressAutocomplete from './AddressAutocomplete';
import { catalogApi } from '../services/api';
import { toast } from 'sonner';

// Nominatim (OSM) forward-geocoder for the "user typed but didn't pick a
// suggestion" case. Only called on submit — never on every keystroke.
const geocodeFallback = async (text) => {
  if (!text || text.trim().length < 4) return null;
  try {
    const r = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(text.trim())}`,
      { headers: { 'Accept-Language': 'en' } },
    );
    const data = await r.json();
    if (Array.isArray(data) && data[0]) {
      return {
        address: data[0].display_name,
        lat: parseFloat(data[0].lat),
        lng: parseFloat(data[0].lon),
      };
    }
  } catch (_) { /* offline */ }
  return null;
};

const CATS = {
  package:   { label: 'Same-day Package',    desc: 'Get an item picked up and dropped off today by a ShipitNow driver.',            icon: Package, tint: 'bg-sky-100 text-sky-700',       ring: 'ring-sky-200' },
  courier:   { label: 'Courier delivery',    desc: 'Fast bike or scooter driver for small parcels.',         icon: Bike,    tint: 'bg-emerald-100 text-emerald-700', ring: 'ring-emerald-200' },
  logistics: { label: 'Business Logistics',  desc: 'Scheduled or bulk shipments handled by our drivers.',    icon: Truck,   tint: 'bg-amber-100 text-amber-700',     ring: 'ring-amber-200' },
};

const distanceKm = (a, b) => {
  if (a?.lat == null || a?.lng == null || b?.lat == null || b?.lng == null) return null;
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

const LogisticsQuickBook = ({ open, onClose, initialCategory = 'package' }) => {
  const nav = useNavigate();
  const [category, setCategory] = useState(initialCategory);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pickup, setPickup] = useState({ address: '', lat: null, lng: null });
  const [drop, setDrop] = useState({ address: '', lat: null, lng: null });
  const [priority, setPriority] = useState(false);
  const [pickedVendorId, setPickedVendorId] = useState(null);
  const [showVendorList, setShowVendorList] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [radiusKm, setRadiusKm] = useState(15);

  useEffect(() => { setCategory(initialCategory); }, [initialCategory, open]);

  // Load admin-configured local radius on mount.
  useEffect(() => {
    if (!open) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/public`)
      .then((r) => r.json())
      .then((s) => {
        const km = Number(s?.local_courier_radius_km);
        if (Number.isFinite(km) && km > 0) setRadiusKm(km);
      })
      .catch(() => {});
  }, [open]);

  // Reset form when dialog re-opens
  useEffect(() => {
    if (open) {
      setPickup({ address: '', lat: null, lng: null });
      setDrop({ address: '', lat: null, lng: null });
      setPriority(false);
      setPickedVendorId(null);
      setShowVendorList(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    catalogApi.vendors({ category })
      .then((v) => { if (alive) setVendors(Array.isArray(v) ? v : []); })
      .catch(() => { if (alive) setVendors([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [category, open]);

  // Rank vendors by distance to pickup, but ONLY include those with real
  // coords within the admin-configured local radius. Vendors without coords
  // or beyond the radius are excluded so users don't get a 5,000 km "courier".
  const ranked = useMemo(() => {
    if (pickup.lat == null || pickup.lng == null) {
      // Before coords are locked, just list all with lat/lng so the user sees
      // there is capacity nearby.
      return vendors
        .filter((v) => v.lat != null && v.lng != null)
        .map((v) => ({ ...v, _distance_km: null }));
    }
    const list = [];
    for (const v of vendors) {
      if (v.lat == null || v.lng == null) continue;
      const km = distanceKm(pickup, v);
      if (km == null || km > radiusKm) continue;
      list.push({ ...v, _distance_km: km });
    }
    list.sort((a, b) => (a._distance_km ?? 9999) - (b._distance_km ?? 9999));
    return list;
  }, [vendors, pickup.lat, pickup.lng, radiusKm]);

  const bestMatch = ranked[0] || null;
  const activeVendor = ranked.find((v) => v.id === pickedVendorId) || bestMatch;

  const submit = async () => {
    if (!activeVendor) { toast.error('No matching drivers available yet — try another category.'); return; }
    setSubmitting(true);
    try {
      let p = pickup;
      let d = drop;
      if (p.lat == null || p.lng == null) {
        const gp = await geocodeFallback(p.address);
        if (gp) { p = gp; setPickup(gp); }
      }
      if (d.lat == null || d.lng == null) {
        const gd = await geocodeFallback(d.address);
        if (gd) { d = gd; setDrop(gd); }
      }
      if (p.lat == null || p.lng == null || d.lat == null || d.lng == null) {
        toast.error('We couldn\u2019t match one of the addresses — please pick from the suggestions.');
        return;
      }
      const q = new URLSearchParams({
        pickup: p.address,
        pickup_lat: String(p.lat),
        pickup_lng: String(p.lng),
        drop: d.address,
        drop_lat: String(d.lat),
        drop_lng: String(d.lng),
        priority: priority ? '1' : '0',
      });
      onClose?.();
      nav(`/book/${activeVendor.id}?${q.toString()}`);
    } finally {
      setSubmitting(false);
    }
  };

  const meta = CATS[category] || CATS.package;
  const CatIcon = meta.icon;

  // A textual address is enough — coords are auto-geocoded on submit if the
  // user didn't pick a suggestion. Keeps the CTA reachable in every browser.
  const canContinue = !!activeVendor
    && pickup.address.trim().length >= 3
    && drop.address.trim().length >= 3;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="max-w-lg sm:max-w-xl p-0 overflow-hidden"
        data-testid="quickbook-modal"
        // Google Places renders `.pac-container` on document.body — clicking
        // a suggestion would trigger Radix's outside-close and blow away the
        // modal. Explicitly ignore interactions that originate inside .pac-*.
        onPointerDownOutside={(e) => {
          const t = e.target;
          if (t && typeof t.closest === 'function' && t.closest('.pac-container')) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          const t = e.target;
          if (t && typeof t.closest === 'function' && t.closest('.pac-container')) {
            e.preventDefault();
          }
        }}
      >
        {/* Header */}
        <div className={`px-6 pt-6 pb-4 ${meta.tint.replace('text-', 'text-')}`}>
          <DialogHeader className="space-y-1.5 text-left">
            <div className="flex items-center gap-3">
              <div className={`h-11 w-11 rounded-xl ${meta.tint} flex items-center justify-center shadow-sm`}>
                <CatIcon className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle className="text-lg font-semibold text-slate-900" data-testid="quickbook-title">
                  {meta.label}
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-500">
                  {meta.desc}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          {/* Category switcher */}
          <div className="mt-4 grid grid-cols-3 gap-2">
            {Object.entries(CATS).map(([key, m]) => {
              const Icon = m.icon;
              const active = category === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setCategory(key); setPickedVendorId(null); }}
                  data-testid={`quickbook-cat-${key}`}
                  className={`flex flex-col items-center gap-1 rounded-xl px-2 py-2 text-[11px] font-medium border transition-all ${
                    active
                      ? `bg-white shadow-sm border-slate-300 ring-2 ${m.ring}`
                      : 'bg-white/60 border-transparent hover:bg-white/80 text-slate-600'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="leading-tight text-center">{m.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 bg-white max-h-[65vh] overflow-y-auto">
          {/* Addresses */}
          <div>
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Pickup</label>
            <AddressAutocomplete
              value={pickup.address}
              onChange={(address, geo) => {
                if (geo?.lat != null && geo?.lng != null) {
                  setPickup({ address, lat: geo.lat, lng: geo.lng });
                } else if (!address) {
                  setPickup({ address: '', lat: null, lng: null });
                } else {
                  // Text typed but not (yet) picked from suggestions — keep
                  // any already-locked coords so autoplay can still submit.
                  setPickup((prev) => ({ ...prev, address }));
                }
              }}
              placeholder="Where should we collect from?"
              icon={MapPin}
            />
            {pickup.address && pickup.lat == null && (
              <div className="text-[11px] text-slate-500 mt-1">Tip: pick a suggestion to lock precise coordinates.</div>
            )}
            {pickup.lat != null && (
              <div className="text-[11px] text-emerald-600 mt-1" data-testid="quickbook-pickup-locked">
                ✓ Address locked
              </div>
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Deliver to</label>
            <AddressAutocomplete
              value={drop.address}
              onChange={(address, geo) => {
                if (geo?.lat != null && geo?.lng != null) {
                  setDrop({ address, lat: geo.lat, lng: geo.lng });
                } else if (!address) {
                  setDrop({ address: '', lat: null, lng: null });
                } else {
                  setDrop((prev) => ({ ...prev, address }));
                }
              }}
              placeholder="Where should we deliver to?"
              icon={MapPin}
            />
            {drop.address && drop.lat == null && (
              <div className="text-[11px] text-slate-500 mt-1">Tip: pick a suggestion to lock precise coordinates.</div>
            )}
            {drop.lat != null && (
              <div className="text-[11px] text-emerald-600 mt-1" data-testid="quickbook-drop-locked">
                ✓ Address locked
              </div>
            )}
          </div>

          {/* Priority */}
          <label className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2.5 cursor-pointer select-none"
                 data-testid="quickbook-priority">
            <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <Zap className="h-4 w-4 text-amber-500" /> Priority delivery
            </span>
            <input
              type="checkbox"
              checked={priority}
              onChange={(e) => setPriority(e.target.checked)}
              className="h-4 w-4 accent-emerald-600"
            />
          </label>

          {/* Vendor pick */}
          <div className="rounded-xl border border-slate-200 p-3">
            <div className="flex items-center justify-between">
              <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                {showVendorList ? 'Choose a driver' : 'Best-match driver'}
              </div>
              {ranked.length > 1 && (
                <button
                  type="button"
                  className="text-xs text-emerald-700 hover:text-emerald-800 font-medium"
                  onClick={() => setShowVendorList((s) => !s)}
                  data-testid="quickbook-override-toggle"
                >
                  {showVendorList ? 'Use best match' : `Change (${ranked.length})`}
                </button>
              )}
            </div>

            {loading ? (
              <div className="flex items-center gap-2 text-sm text-slate-500 mt-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading drivers…
              </div>
            ) : ranked.length === 0 ? (
              <div className="mt-2 text-sm text-slate-500" data-testid="quickbook-no-local">
                {(pickup.lat != null)
                  ? `No local drivers within ${radiusKm} km of your pickup yet — try another category or check back soon.`
                  : 'Set a pickup address to see local drivers.'}
              </div>
            ) : showVendorList ? (
              <div className="mt-2 max-h-40 overflow-y-auto divide-y divide-slate-100" data-testid="quickbook-vendor-list">
                {ranked.map((v) => {
                  const active = (activeVendor?.id === v.id);
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => { setPickedVendorId(v.id); setShowVendorList(false); }}
                      className={`w-full flex items-center justify-between gap-2 py-2 px-1 text-left rounded-md transition-colors ${
                        active ? 'bg-emerald-50' : 'hover:bg-slate-50'
                      }`}
                      data-testid={`quickbook-vendor-${v.id}`}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {v.system_vendor ? 'ShipitNow driver' : v.name}
                        </div>
                        <div className="text-[11px] text-slate-500">
                          {v.system_vendor
                            ? 'Nearest available driver'
                            : ((v._distance_km != null ? `${v._distance_km.toFixed(1)} km from pickup` : (v.city || '—'))
                                + (v.rating ? ` · ⭐ ${Number(v.rating).toFixed(1)}` : ''))}
                        </div>
                      </div>
                      {active && <span className="text-[11px] font-semibold text-emerald-700">Selected</span>}
                    </button>
                  );
                })}
              </div>
            ) : activeVendor ? (
              <div className="mt-2 flex items-center gap-3" data-testid="quickbook-best-match">
                <div className={`h-10 w-10 rounded-lg ${meta.tint} flex items-center justify-center shrink-0`}>
                  <CatIcon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">
                    {activeVendor.system_vendor ? 'ShipitNow driver' : activeVendor.name}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {activeVendor.system_vendor
                      ? "We'll match you with the nearest available driver."
                      : (activeVendor._distance_km != null
                          ? `${activeVendor._distance_km.toFixed(1)} km from pickup`
                          : (pickup.lat == null ? 'Set pickup for distance ranking' : 'Distance unknown'))}
                    {activeVendor.rating && !activeVendor.system_vendor ? ` · ⭐ ${Number(activeVendor.rating).toFixed(1)}` : ''}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 pt-2 bg-white border-t border-slate-100 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
            data-testid="quickbook-cancel"
          >
            <ArrowLeft className="h-4 w-4" /> Cancel
          </button>
          <Button
            onClick={submit}
            disabled={!canContinue || submitting}
            className="h-11 px-5 bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="quickbook-continue"
          >
            {submitting ? (
              <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Locating…</>
            ) : (
              <>Continue to sign & book<ChevronRight className="h-4 w-4 ml-1" /></>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LogisticsQuickBook;
